import React, { useEffect, useState } from 'react';
import Table from '../../../components/Table';
import TableHeader from '../../../components/Table/TableHeader';
import Button from '../../../components/CustomButtons/Button';
import { gql } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@apollo/client';
import Tile from 'components/Tile/Tile';
import { useSelector } from 'react-redux';
import { Box, Grid } from '@mui/material';
import TableSearch from '../../../components/Table/TableSearch';
import { GET_CS_LIST_WITHOUT_ASSIGNMENT_TITLES } from '../../../services/creditSubmissions';
import { StoreOutlined } from '@mui/icons-material';
import AddDealer from './AddDealer';
import { PageContainer } from '../../../components/shared/Page/PageContainer';

const _ = require('lodash');

const useStyles = makeStyles({
  left: {
    textAlign: 'left',
  },
  right: {
    textAlign: 'right',
  },
  vpStatusActive: {
    padding: '4px 8px',
    color: '#2E7D32',
    backgroundColor: '#DCEDC8',
    width: 'fit-content',
    minWidth: 76,
    textAlign: 'center',
    borderRadius: 8,
    margin: 0,
  },
  vpStatusBlocked: {
    padding: '4px 8px',
    color: '#C62828',
    backgroundColor: '#FFCDD2',
    width: 'fit-content',
    minWidth: 76,
    textAlign: 'center',
    borderRadius: 8,
    margin: 0,
  },
  vpStatusStaged: {
    padding: '4px 8px',
    color: '#424242',
    backgroundColor: '#EEEEEE',
    width: 'fit-content',
    minWidth: 76,
    textAlign: 'center',
    borderRadius: 8,
    margin: 0,
  },
  vpStatusInvited: {
    padding: '4px 8px',
    color: '#1565C0',
    backgroundColor: '#BBDEFB',
    width: 'fit-content',
    minWidth: 76,
    textAlign: 'center',
    borderRadius: 8,
    margin: 0,
  },
});

const QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER = gql`
  query vendorProfilesByDynamicsLenderAccountId($dynamicsAccountId: ID!) {
    vendorProfilesByDynamicsLenderAccountId(dynamicsAccountId: $dynamicsAccountId) {
      id
      dynamicsId
      name
      accountId
      dynamicsAccountId
      vpStatus
    }
  }
`;

const statusTitles = {
  Active: 'Dealer has successfully registered contact.',
  Invited: 'Dealer contact has been invited to register to Trnsact.',
  Staged: 'Dealer is connected but there are no contacts invited.',
};

const DealerManagementList = () => {
  const classes = useStyles();
  const account = useSelector(state => state.account);
  const vendorProfile = useSelector(state => state.vp);
  const searchRef = React.useRef({ prevSearch: undefined, currentSearch: '' });
  const [creditSubmissionsDealers, setCreditSubmissionsDealers] = useState([]);
  const [savedListSettings, setSavedListSettings] = useState({ page: 0, pageSize: 5 });
  const [filteredDealerList, setFilteredDealerList] = useState([]);
  const [vendorProfilesAssociatedToLender, setVendorProfilesAssociatedToLender] = useState();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSizeValue, setPageSizeValue] = useState(20);
  const [openAddModal, setOpenAddModal] = useState(false);

  const { loading: isLoadingCSList, data: csListData, error } = useQuery(GET_CS_LIST_WITHOUT_ASSIGNMENT_TITLES);

  const {
    loading: isLoadingVendorProfilesAssociatedToLender,
    data: vendorProfilesAssociatedToLenderData,
    refetch: refetchVPs,
  } = useQuery(QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER, {
    variables: {
      dynamicsAccountId: account?.dynamicsAccountId,
    },
    context: { authRequired: true },
    fetchPolicy: 'no-cache',
  });

  const filterDealerProfileList = dealerProfileList => {
    const { currentSearch } = searchRef.current;

    if (currentSearch.length === 0) {
      return dealerProfileList;
    }

    const lowerCaseSearch = currentSearch.toLowerCase().trim();
    const searchData = dealerProfileList.filter(el => {
      if (typeof el.dealerName === 'string') {
        return (el.dealerName || '').toLowerCase().includes(lowerCaseSearch);
      }

      return false;
    });

    return [...searchData];
  };

  useEffect(() => {
    if (csListData?.creditSubmissions) {
      const csList = csListData?.creditSubmissions;
      if (csList.length > 0) {
        let dealers = Object.entries(_.groupBy(csList, cs => cs.dealerName)).map(e => ({
          dealerName: e[0],
          dealerId: e[1][0].dealer,
          submissions: e[1],
        }));
        dealers = [
          ...dealers.map(dealer => {
            const contacts = _.groupBy(dealer.submissions, sub => sub.contactName);
            const decisions = _.groupBy(dealer.submissions, sub => sub.decision);
            let decisionsCount = Object.entries(decisions).map(e => ({
              decision: e[0],
              count: e[1].length,
            }));

            return {
              dealerName: dealer.dealerName,
              dealerId: dealer.dealerId, // dynamicsAccountId
              contactsCount: Object.keys(contacts).filter(e => e !== 'null').length,
              contacts,
              decisions,
              decisionsCount,
              dealerStatus: '',
            };
          }),
        ];
        setCreditSubmissionsDealers(dealers.filter(d => d.dealerName !== 'null'));
      }
    }
  }, [csListData]);

  useEffect(() => {
    let allDealers = _(vendorProfilesAssociatedToLender).concat(creditSubmissionsDealers).compact().value();
    allDealers = allDealers
      .map(vp => {
        if (creditSubmissionsDealers.find(v => v.dealerId === vp.dealerId)) {
          if (vp.dealerStatus === '') return undefined;
          let credVp = creditSubmissionsDealers.find(v => v.dealerId === vp.dealerId) || {};
          return { ...vp, ...credVp, dealerStatus: vp.dealerStatus };
        }
        return vp;
      })
      .filter(vp => vp);
    setFilteredDealerList(filterDealerProfileList(allDealers));
  }, [creditSubmissionsDealers, vendorProfilesAssociatedToLender, searchRef.current.currentSearch]);

  useEffect(() => {
    if (_.isEmpty(vendorProfilesAssociatedToLenderData)) {
      return;
    }
    const vendorProfilesAssociatedToLenderToSet = _.map(
      vendorProfilesAssociatedToLenderData.vendorProfilesByDynamicsLenderAccountId,
      vp => {
        return {
          dealerName: vp.name,
          dealerId: vp.dynamicsAccountId,
          dealerStatus: vp.vpStatus || '',
          decisions: [],
          decisionsCount: [], // will be overwritten if creditsubmissions exist
        };
      }
    );
    setVendorProfilesAssociatedToLender(vendorProfilesAssociatedToLenderToSet);
  }, [vendorProfilesAssociatedToLenderData]);

  return (
    <PageContainer error={error} loading={isLoadingCSList || isLoadingVendorProfilesAssociatedToLender}>
      <Box>
        <Grid>
          <Grid item xs={12}>
            <Tile>
              <TableHeader
                filterComps={
                  <>
                    <TableSearch
                      setPageNumber={setPageNumber}
                      setSavedListSettings={setSavedListSettings}
                      searchRef={searchRef}
                      savedListSettings={savedListSettings}
                    />
                  </>
                }
              >
                <Button color="primary" size="sm" onClick={() => setOpenAddModal(true)}>
                  <StoreOutlined />
                  Add Dealer
                </Button>
              </TableHeader>
              <Table
                color="primary"
                page={pageNumber}
                data={filteredDealerList}
                getTrProps={(state, rowInfo, column, instance) => ({
                  style: { cursor: 'pointer' },
                  onClick: () => {
                    console.log(rowInfo.original);
                    if (rowInfo.original?.dealerId) {
                      window.location.href = `/dealers/${rowInfo.original.dealerId}`;
                    }
                  },
                })}
                columns={[
                  {
                    Header: 'Dealer Name',
                    accessor: 'dealerName',
                    Cell: props => props.value.replace('VP - ', ''),
                  },
                  {
                    Header: 'Status',
                    accessor: 'dealerStatus',
                    id: 'dealerStatus',
                    Cell: props => {
                      console.log(props.value);
                      let status = props.value?.charAt(0).toUpperCase() + props.value?.slice(1) || '';
                      return (
                        <div title={statusTitles[status]} className={classes[`vpStatus${status}`]}>
                          {status}
                        </div>
                      );
                    },
                  },
                  {
                    Header: 'Transactions Stats.',
                    accessor: 'decisionsCount',
                    Cell: props => (
                      <div>
                        {props.value.map((el, key) => {
                          return (
                            <p key={key}>
                              {el?.decision}
                              {': '}
                              <strong>{el?.count}</strong>
                            </p>
                          );
                        })}
                      </div>
                    ),
                    filterable: false,
                    sortable: false,
                  },
                ]}
                onPageChange={changePage => {
                  setPageNumber(changePage);
                }}
                onPageSizeChange={changePageSize => {
                  setPageSizeValue(changePageSize);
                  setPageNumber(0);
                }}
                pageSize={pageSizeValue}
                defaultPageSize={savedListSettings.pageSize}
                showPaginationTop={false}
                showPaginationBottom={true}
                customCellClasses={[classes.right]}
                customClassesForCells={[5]}
                className=" -highlight"
              />
            </Tile>
          </Grid>
        </Grid>
        <AddDealer open={openAddModal} handleClose={() => setOpenAddModal(false)} refetchVPs={refetchVPs} />
      </Box>
    </PageContainer>
  );
};

export default DealerManagementList;
